// "Forecast" on a sell item (item panel, the Forecast page and the Purchasing plan's detail): the chart of
// 24 months of sales with the days out of stock shaded and the next 12 months' forecast, the numbers, the
// why, monthly overrides typed in place and "forecast like another item". All from the synced forecast
// (lib/forecast.ts), so an override or a like shows at once.
import { ChevronDown, ChevronRight } from "lucide-react";
import { type ComponentProps, type ReactNode, useMemo, useRef, useState } from "react";
import type { Item } from "../../../shared/db.gen.ts";
import {
  explainForecast,
  explainMonth,
  factorText,
  type ItemForecast,
  type MonthForecast,
  monthDays,
  monthIso,
  monthNum,
  type Quality,
  quality,
  SEASON_LABELS,
} from "../../../shared/forecast.ts";
import { fmt, monthLabel } from "../../../shared/planning.ts";
import { useForecasts } from "../lib/forecast.ts";
import { typedCount } from "../lib/format.ts";
import { can, mutate } from "../lib/mutate.ts";
import { saveItemPlan } from "../lib/planning.ts";
import { Link } from "../lib/router.tsx";
import { useRow, useRows } from "../lib/sync.ts";
import { Badge, CommitInput, cx, Select, StatTile } from "../ui/controls.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";
import { Section } from "../ui/RecordPanel.tsx";
import { toast } from "../ui/toast.tsx";

export const QUALITY_TONE: Record<Quality["label"], "ok" | "warn" | "danger" | "neutral"> = { good: "ok", fair: "warn", poor: "danger", new: "neutral" };
export const QUALITY_LABEL: Record<Quality["label"], string> = { good: "Good", fair: "Fair", poor: "Poor", new: "New" };

/** The method chip: where the numbers come from. */
export function methodChip(f: ItemForecast): { label: string; tone: "neutral" | "accent" | "warn"; hint: string } {
  if (f.from === "like") return { label: `Like ${f.like?.sku}`, tone: "accent", hint: `Forecast like ${f.like?.sku} at ${f.like?.pct}%` };
  if (f.from === "summary") return { label: "Raw", tone: "warn", hint: "The nightly forecast hasn't run for it yet: raw sales, no seasonality or trend" };
  if (f.from === "none") return { label: "No sales", tone: "neutral", hint: "No sales to forecast from" };
  if (f.stopped) return { label: "Stopped", tone: "neutral", hint: "Out of stock 180+ days in a row" };
  if (f.base?.method === "raw") return { label: "Raw", tone: "warn", hint: "No stock history for these dates: raw sales" };
  return {
    label: SEASON_LABELS[f.seasonFrom],
    tone: "neutral",
    hint: `Stockout-adjusted; seasonality: ${f.seasonFrom === "none" ? "none" : f.seasonFrom.replace("_", " ")}`,
  };
}

export function ForecastSection({ item, open = false }: { item: Item; open?: boolean }) {
  const forecasts = useForecasts();
  const f = forecasts.byItem.get(item.id);
  const [whyOpen, setWhyOpen] = useState(open);
  const monthRef = useRef<HTMLDivElement>(null);
  if (!f) return null; // archived or not a sell item
  const q = quality(f.base);
  const chip = methodChip(f);
  return (
    <Section title="Forecast">
      <div className="flex flex-wrap items-center gap-1.5 px-2 pb-2">
        <span title={chip.hint}>
          <Badge tone={chip.tone}>{chip.label}</Badge>
        </span>
        {f.trend !== 1 && <Badge>Trend {factorText(f.trend)}</Badge>}
        <span title={q.text}>
          <Badge tone={QUALITY_TONE[q.label]}>Accuracy: {QUALITY_LABEL[q.label]}</Badge>
        </span>
        <Link to={`/forecast?item=${item.id}`} className="ml-auto text-[12px] text-accent-text hover:underline">
          Open in Forecast
        </Link>
      </div>
      <div className="grid grid-cols-4 gap-2 px-2 pb-2">
        <StatTile label="Next 30 days" value={fmt(Math.round(f.next30))} />
        <StatTile label="Next 90 days" value={fmt(Math.round(f.next90))} />
        <StatTile label="Next 12 months" value={fmt(Math.round(f.next365))} />
        <StatTile label="Base / day" value={fmt(f.level)} hint="Units a day at an average time of year" />
      </div>
      {f.flags.length > 0 && (
        <ul className="mx-2 mb-2 rounded-md bg-warn-soft px-3 py-1.5 text-[12px] text-warn">
          {f.flags.map((x) => (
            <li key={x.code}>{x.message}</li>
          ))}
        </ul>
      )}
      {/* A forecast month clicked on the chart: its Override cell, which a click edits */}
      <ForecastChart f={f} onMonth={(m) => monthRef.current?.querySelector<HTMLElement>(`[data-month="${m}"]`)?.click()} />
      <div className="px-2 pb-1">
        <button type="button" onClick={() => setWhyOpen(!whyOpen)} className="flex items-center gap-1 text-[12px] text-muted hover:text-text">
          {whyOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />} Why
        </button>
        {whyOpen && (
          <ol className="mt-1 mb-1 ml-1 list-decimal space-y-0.5 pl-4 text-[12px] text-muted">
            {[...explainForecast(f), q.text].map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
        )}
      </div>
      <div ref={monthRef}>
        <MonthOverrides f={f} />
      </div>
      <ForecastLike item={item} f={f} />
    </Section>
  );
}

// The chart ------------------------------------------------------------------------------------------------

const W = 436;
const H = 150;
const PAD = { left: 30, right: 6, top: 8, bottom: 18 };
const SHORT = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

/** Clean round steps for the y axis: 0 and up to three ticks. */
function ticks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / 3;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((s) => s * pow).find((s) => s >= raw) ?? raw;
  return Array.from({ length: Math.ceil(max / step) + 1 }, (_, i) => i * step);
}
const compact = (n: number) => (n >= 1000 ? `${fmt(Math.round(n / 100) / 10)}K` : fmt(n));

/** A column with a rounded top and a square base. */
const column = (x: number, y: number, w: number, h: number) => {
  if (h <= 0) return "";
  const r = Math.min(2, w / 2, h);
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
};

type Slot = { m: number; sold: number | null; partial: boolean; out: number | null; forecast: number | null; override: boolean };

/**
 * 24 months of sales (columns; days out of stock shade the month), this month to date, and the next 12
 * months' forecast (a line). Hovering a month shows its numbers; clicking a forecast month edits it.
 */
export function ForecastChart({ f, onMonth }: { f: ItemForecast; onMonth?: (month: string) => void }) {
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const b = f.base;
  const slots = useMemo(() => {
    const now = monthNum(f.months[0].month);
    const from = now - 24;
    const histFrom = b ? monthNum(b.months_from) : now;
    const out: Slot[] = [];
    for (let m = from; m < now + 12; m++) {
      const k = m - histFrom;
      const hasHist = !!b && k >= 0 && k < b.units_by_month.length;
      const fm = m >= now ? f.months[m - now] : undefined;
      out.push({
        m,
        sold: hasHist ? Math.max(0, b.units_by_month[k]) : m <= now ? 0 : null,
        partial: m === now,
        out: hasHist && b.out_by_month[k] >= 0 ? b.out_by_month[k] : null,
        forecast: fm ? fm.units : null,
        override: !!fm && fm.override !== null,
      });
    }
    return out;
  }, [f, b]);
  const max = Math.max(1, ...slots.map((s) => Math.max(s.sold ?? 0, s.forecast ?? 0)));
  const ys = ticks(max);
  const top = ys.at(-1) ?? max;
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const slotW = plotW / slots.length;
  const barW = Math.min(24, slotW - 2);
  const x = (i: number) => PAD.left + i * slotW;
  const y = (v: number) => PAD.top + plotH - (v / top) * plotH;
  const line = slots
    .map((s, i) => (s.forecast === null ? null : `${x(i) + slotW / 2},${y(s.forecast)}`))
    .filter(Boolean)
    .join(" ");
  const h = hover === null ? null : slots[hover];

  return (
    <div className="px-2 pb-2">
      <div className="mb-1 flex items-center gap-3 text-[11px] text-muted">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2 rounded-t-[2px] bg-[var(--series-1)]" /> Sold
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-0.5 w-3 rounded bg-[var(--series-2)]" /> Forecast
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block size-2.5 rounded-sm bg-warn/30" /> Days out of stock
        </span>
        <span className="ml-auto flex items-center gap-2">
          <button type="button" className="text-faint hover:text-text" onClick={() => setTable(!table)}>
            {table ? "Chart" : "Table"}
          </button>
        </span>
      </div>
      {table ? (
        <MonthTable slots={slots} f={f} />
      ) : (
        <div className="relative">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="block w-full"
            role="img"
            aria-label={`Units sold per month for 24 months and the forecast for the next 12. ${slots
              .map(
                (s) =>
                  `${monthLabel(monthIso(s.m))}: ${s.sold !== null ? `${fmt(s.sold)} sold` : ""}${s.forecast !== null ? ` forecast ${fmt(Math.round(s.forecast))}` : ""}`,
              )
              .join("; ")}`}
            onMouseLeave={() => setHover(null)}
          >
            {ys.map((t) => (
              <g key={t}>
                <line x1={PAD.left} x2={W - PAD.right} y1={y(t)} y2={y(t)} stroke="var(--line)" strokeWidth={1} />
                <text x={PAD.left - 4} y={y(t) + 3} textAnchor="end" className="fill-[var(--faint)] text-[9px] tabular-nums">
                  {compact(t)}
                </text>
              </g>
            ))}
            {slots.map((s, i) => (
              <g key={s.m}>
                {s.out !== null && s.out > 0 && (
                  <rect x={x(i)} y={PAD.top} width={slotW} height={plotH} fill="var(--warn)" opacity={0.08 + 0.3 * (s.out / monthDays(s.m))} />
                )}
                {hover === i && <rect x={x(i)} y={PAD.top} width={slotW} height={plotH} fill="var(--text)" opacity={0.05} />}
                {s.sold !== null && s.sold > 0 && (
                  <path d={column(x(i) + (slotW - barW) / 2, y(s.sold), barW, y(0) - y(s.sold))} fill="var(--series-1)" opacity={s.partial ? 0.55 : 1} />
                )}
                {s.m % 12 === 0 || i === 0 ? (
                  <text x={x(i) + slotW / 2} y={H - 5} textAnchor="middle" className="fill-[var(--muted)] text-[9px]">
                    {monthLabel(monthIso(s.m)).slice(-4)}
                  </text>
                ) : s.m % 3 === 0 ? (
                  <text x={x(i) + slotW / 2} y={H - 5} textAnchor="middle" className="fill-[var(--faint)] text-[9px]">
                    {SHORT[s.m % 12]}
                  </text>
                ) : null}
              </g>
            ))}
            <line x1={PAD.left} x2={W - PAD.right} y1={y(0)} y2={y(0)} stroke="var(--line-strong)" strokeWidth={1} />
            {line && <polyline points={line} fill="none" stroke="var(--series-2)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
            {slots.map((s, i) =>
              s.forecast === null ? null : (
                <circle
                  key={`f${s.m}`}
                  cx={x(i) + slotW / 2}
                  cy={y(s.forecast)}
                  r={s.override ? 4.5 : hover === i ? 4 : 3}
                  fill={s.override ? "var(--surface)" : "var(--series-2)"}
                  stroke={s.override ? "var(--series-2)" : "var(--surface)"}
                  strokeWidth={2}
                />
              ),
            )}
            {/* Hit targets: the whole month, not just its marks. */}
            {slots.map((s, i) => (
              <rect
                key={`h${s.m}`}
                x={x(i)}
                y={0}
                width={slotW}
                height={H}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onClick={() => s.forecast !== null && onMonth?.(monthIso(s.m))}
                className={s.forecast !== null && onMonth ? "cursor-pointer" : undefined}
              />
            ))}
          </svg>
          {h && (
            <div
              className="pointer-events-none absolute top-1 z-10 w-56 rounded-md border border-line bg-surface px-2.5 py-1.5 text-[11px] shadow-pop"
              style={hover !== null && x(hover) > W / 2 ? { left: 0 } : { right: 0 }}
            >
              <div className="font-medium text-text">{monthLabel(monthIso(h.m))}</div>
              {h.sold !== null && (
                <div className="flex items-center gap-1.5">
                  <span className="inline-block h-0.5 w-2.5 bg-[var(--series-1)]" />
                  <span className="font-semibold text-text">{fmt(h.sold)}</span>
                  <span className="text-muted">sold{h.partial ? " so far" : ""}</span>
                </div>
              )}
              {h.forecast !== null && (
                <div className="flex items-center gap-1.5">
                  <span className="inline-block h-0.5 w-2.5 bg-[var(--series-2)]" />
                  <span className="font-semibold text-text">{fmt(Math.round(h.forecast))}</span>
                  <span className="text-muted">forecast{h.override ? " (override)" : ""}</span>
                </div>
              )}
              <div className="text-muted">
                {h.out === null
                  ? h.sold !== null
                    ? "No stock history"
                    : ""
                  : h.out
                    ? `Out of stock ${h.out} of ${monthDays(h.m)} days`
                    : "In stock all month"}
              </div>
              {h.forecast !== null && (
                <div className="mt-0.5 text-faint">{explainMonth(f, f.months[h.m - monthNum(f.months[0].month)]).replace(/^\w{3} \d{4}: /, "")}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The chart's months as a table: 24 months sold, this month so far and the next 12 months' forecast (* an override). */
function MonthTable({ slots, f }: { slots: Slot[]; f: ItemForecast }) {
  const rows = useMemo(() => slots.map((s) => ({ ...s, id: String(s.m) })), [slots]);
  const columns: Column<Slot & { id: string }>[] = [
    {
      key: "month",
      header: "Month",
      width: "minmax(90px,1fr)",
      value: (s) => s.m,
      text: (s) => monthLabel(monthIso(s.m)),
      cell: (s) => monthLabel(monthIso(s.m)),
    },
    {
      key: "sold",
      header: "Sold",
      width: "96px",
      align: "right",
      value: (s) => s.sold,
      cell: (s) => (s.sold === null ? "" : `${fmt(s.sold)}${s.partial ? " so far" : ""}`),
    },
    {
      key: "out",
      header: "Days out",
      width: "80px",
      align: "right",
      value: (s) => s.out,
      cell: (s) => <span className="text-muted">{s.out === null ? (s.sold === null ? "" : "—") : s.out}</span>,
    },
    {
      key: "forecast",
      header: "Forecast",
      headerTitle: "* an override",
      width: "90px",
      align: "right",
      value: (s) => (s.forecast === null ? null : Math.round(s.forecast)),
      cell: (s) => (
        <span title={s.forecast === null ? undefined : explainMonth(f, f.months[s.m - monthNum(f.months[0].month)])}>
          {s.forecast === null ? "" : `${fmt(Math.round(s.forecast))}${s.override ? "*" : ""}`}
        </span>
      ),
    },
  ];
  return (
    <DataTable
      fit={8}
      rows={rows}
      columns={columns}
      defaultSort={{ key: "month" }}
      rowNoun={["month", "months"]}
      exportName="Forecast months"
      pageFilters={false}
    />
  );
}

// Overrides and forecast like ---------------------------------------------------------------------------------

/** A month's box: saves on Enter or blur (Esc puts it back), its text selected when it's entered. */
const CellInput = ({ month, ...props }: ComponentProps<typeof CommitInput> & { month?: string }) => (
  <CommitInput data-month={month} onFocus={(e) => e.currentTarget.select()} {...props} />
);

/** The next 12 months: the forecast and its reasoning, and an override typed over any month. */
function MonthOverrides({ f }: { f: ItemForecast }) {
  const overrides = useRows("forecast_override");
  const editable = can("forecast_override.create");
  const mine = overrides.filter((o) => o.item_id === f.itemId);
  const find = (month: string) => mine.find((o) => o.month.slice(0, 7) === month.slice(0, 7));
  const later = mine.filter((o) => !f.months.some((m) => m.month.slice(0, 7) === o.month.slice(0, 7))).sort((a, b) => a.month.localeCompare(b.month));
  const setUnits = (month: string, text: string) => {
    const existing = find(month);
    const n = typedCount(text);
    if (n === undefined) return toast.error("Enter a number of units, or leave it empty to use the forecast");
    if (n === null) return existing && mutate("forecast_override.remove", { id: existing.id });
    if (existing) return mutate("forecast_override.update", { id: existing.id, patch: { units: n } });
    return mutate("forecast_override.create", { id: crypto.randomUUID(), item_id: f.itemId, month, units: n });
  };
  const rows = useMemo(() => f.months.map((m) => ({ ...m, id: m.month })), [f.months]);
  const columns: Column<MonthForecast & { id: string }>[] = [
    { key: "month", header: "Month", width: "minmax(80px,1fr)", text: (m) => monthLabel(m.month), cell: (m) => monthLabel(m.month) },
    {
      key: "forecast",
      header: "Forecast",
      headerTitle: "Base rate × season × adjustments (* adjusted)",
      width: "84px",
      align: "right",
      value: (m) => Math.round(m.base * m.factor),
      cell: (m) => {
        const computed = m.base * m.factor;
        return (
          <span className={cx("font-mono", find(m.month) ? "text-faint line-through" : "")} title={explainMonth(f, { ...m, override: null, units: computed })}>
            {fmt(Math.round(computed))}
            {m.factor !== 1 && <span className="ml-0.5 text-accent-text">*</span>}
          </span>
        );
      },
    },
    {
      key: "override",
      header: "Override",
      headerTitle: "Units that replace the month's forecast; empty uses the forecast",
      width: "84px",
      align: "right",
      value: (m) => find(m.month)?.units ?? null,
      cell: (m) => {
        const o = find(m.month);
        // data-month: the chart's click on a month edits this cell
        return (
          <span data-month={m.month} className={cx("block w-full text-right font-mono", o ? "text-accent-text" : "text-faint")}>
            {o ? fmt(o.units) : "—"}
          </span>
        );
      },
      edit: { can: () => editable, get: (m) => String(find(m.month)?.units ?? ""), save: (m, text) => void setUnits(m.month, text) },
    },
    {
      key: "note",
      header: "Note",
      headerTitle: "Why (launch, sale…)",
      width: "minmax(100px,2fr)",
      value: (m) => find(m.month)?.note ?? "",
      cell: (m) => (
        <span className="truncate text-muted" title={find(m.month)?.note}>
          {find(m.month)?.note}
        </span>
      ),
      edit: {
        can: (m) => editable && !!find(m.month),
        get: (m) => find(m.month)?.note ?? "",
        save: (m, text) => {
          const o = find(m.month);
          if (o) mutate("forecast_override.update", { id: o.id, patch: { note: text } });
        },
      },
    },
  ];
  return (
    <div className="px-2 pt-1 pb-2">
      <div className="flex items-center gap-2 pb-1">
        <span className="text-[11px] font-medium tracking-wide text-faint uppercase">Next 12 months</span>
      </div>
      <DataTable
        fit
        rows={rows}
        columns={columns}
        defaultSort={{ key: "month" }}
        editing={editable}
        rowNoun={["month", "months"]}
        exportName="Forecast overrides"
        pageFilters={false}
      />
      {later.length > 0 && (
        <div className="pt-1 text-[12px] text-muted">
          Later:{" "}
          {later.map((o) => (
            <span key={o.id} className="mr-2">
              {monthLabel(o.month)} {fmt(o.units)}
              {editable && (
                <button
                  type="button"
                  className="ml-1 text-faint hover:text-danger"
                  onClick={() => mutate("forecast_override.remove", { id: o.id })}
                  title="Remove"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <div className="pt-1 text-[11px] text-faint">* adjusted (see Why). An override replaces the month, adjustments included; B2B orders are on top.</div>
    </div>
  );
}

/** "Forecast like SKU X at N%", for new items. */
function ForecastLike({ item, f }: { item: Item; f: ItemForecast }) {
  const ip = useRow("item_plan", item.id);
  const items = useRows("item");
  const forecasts = useForecasts();
  const editable = can("item_plan.update");
  const options = useMemo(
    () => [
      { value: "", label: "Its own sales" },
      ...items
        .filter((i) => i.id !== item.id && forecasts.byItem.get(i.id)?.base)
        .sort((a, b) => a.sku.localeCompare(b.sku))
        .map((i) => ({ value: i.id, label: `${i.sku} · ${i.name}` })),
    ],
    [items, item.id, forecasts],
  );
  const row: ReactNode = (
    <div className="flex items-center gap-2 px-2 pb-1 text-[12px]">
      <span className="w-24 shrink-0 text-muted">Forecast like</span>
      <div className="min-w-0 flex-1">
        <Select
          value={ip?.like_item_id ?? ""}
          options={options}
          disabled={!editable}
          onChange={(e) => saveItemPlan(item.id, { like_item_id: e.target.value || null })}
          className="border-transparent bg-transparent hover:border-line"
        />
      </div>
      {ip?.like_item_id && (
        <>
          <span className="text-muted">at</span>
          <div className="w-16 shrink-0">
            <CellInput
              value={String(ip.like_pct ?? 100)}
              disabled={!editable}
              className="text-right font-mono"
              onCommit={(t) => {
                const n = typedCount(t);
                if (!n) return toast.error("Enter a percentage above 0");
                saveItemPlan(item.id, { like_pct: n });
              }}
            />
          </div>
          <span className="text-muted">%</span>
        </>
      )}
    </div>
  );
  return (
    <>
      {row}
      {f.from === "like" && f.base && <div className="px-2 pb-1 text-[11px] text-faint">Its own history is still kept; clear this to go back to it.</div>}
    </>
  );
}
